/*
  Same-page jumps from the command palette (02 #9).

  Pages read their deep link (`/procurement?lpo=…`, `/inventory?tab=stock&search=…`) when they mount. A palette
  result for the page that is already open only changes the URL, so the page would not notice. The palette
  therefore navigates with `state.paletteNav` set when the target path is the current one, and the shell keys the
  routed page on `usePaletteNavKey()` (App.js → ProtectedRoute) so that page remounts and reads the new link,
  exactly like a fresh load. Every other navigation (the page's own setSearchParams, links, back / forward to an
  entry without the marker) keeps the key, so nothing else ever remounts.

    const navKey = usePaletteNavKey();
    <Layout><Fragment key={navKey}>{page}</Fragment></Layout>

    const go = useSamePageAwareNavigate();     // what the palette uses
    go('/procurement?lpo=LPO-2025-002');
*/
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/** Location-state key set when the palette opens a link on the page that is already open. */
export const PALETTE_NAV_STATE = 'paletteNav';

let sequence = 0;

/** A React key for the routed page that changes only on a marked same-page jump. */
export function usePaletteNavKey() {
  const location = useLocation();
  const marker = location.state?.[PALETTE_NAV_STATE] || '';
  const [key, setKey] = useState(marker);
  if (marker && marker !== key) {
    setKey(marker); // adjusting state while rendering: React re-renders at once, before anything is committed
    return marker;
  }
  return key;
}

/** navigate(url) that marks the jump when `url` is on the page that is already open. */
export function useSamePageAwareNavigate() {
  const navigate = useNavigate();
  const location = useLocation();
  return (url) => {
    if (!url) return;
    let samePage = false;
    try {
      samePage = new URL(url, window.location.origin).pathname === location.pathname;
    } catch {
      samePage = false;
    }
    sequence += 1;
    navigate(url, samePage ? { state: { [PALETTE_NAV_STATE]: `${Date.now()}.${sequence}` } } : undefined);
  };
}
